/* eslint-disable import/prefer-default-export */
import { computed } from "vue";
import { defineStore } from "pinia";

import useLocalStorage from "@/composables/storage/useLocalStorage.js";
import { useGlobalStore } from "@/stores/globalStore";

import useFormValidation from "@/composables/validations/useFormValidation";
import filters from "@/composables/store/filters";
import usePaymentFilters from "@/composables/filters/usePaymentFilters";
import useBlockchainFilters from "@/composables/filters/useBlockchainFilters";
import useOrderEndpoints from "@/composables/api/useOrderEndpoints";

const { checkLocalStorage, checkLocalStorageByKeys, bindLocalStorage } =
  useLocalStorage();
const {
  validateFiatField,
  validateCoinField,
  validateWalletAddressField,
  validateWalletAddressTag,
} = useFormValidation();
const { filterPaymentLimit, filterPaymentProvider } = usePaymentFilters();
const { getActiveCoinData } = useBlockchainFilters();
const { formHasErrors, getErrorByField, getFormPayload, getErrorInForm } =
  filters();
const { getFiats, getCoins } = useOrderEndpoints();

const formObject = {
  fiatAmount: {
    value: "",
    error: "",
    required: true,
    fromApi: true,
    validator: validateFiatField,
  },
  fiatType: {
    value: "USD",
    required: true,
    fromApi: false,
  },
  coinAmount: {
    value: "",
    error: "",
    required: true,
    fromApi: false,
    validator: validateCoinField,
  },
  coinType: {
    value: "ETH",
    required: true,
    fromApi: false,
  },
  lockFiat: {
    value: false,
    required: true,
  },
  walletAddress: {
    value: null,
    error: null,
    required: true,
    fromApi: false,
    validator: computed(() => validateWalletAddressField),
  },
  blockchain: {
    value: "ETH",
    required: true,
    fromApi: false,
  },
  walletAddressTag: {
    value: "",
    required: false,
    fromApi: false,
    validator: validateWalletAddressTag,
  },
  payment: {
    value: null,
    fromApi: false,
    required: true,
  },
};
const { payment, ...rest } = formObject;

export const useNFTOrderFormStore = defineStore({
  id: "nftOrder",
  state: () => ({
    form: {
      ...{
        payment: checkLocalStorageByKeys("nftOrder", "payment", formObject),
      },
      ...rest,
    },
    coinData: {
      data: checkLocalStorage("coinData", []),
      coinType: checkLocalStorage("coinType", formObject.coinType.value),
    },
    fiatData: {
      data: checkLocalStorage("fiatData", []),
      fiatType: checkLocalStorage("fiatType", formObject.fiatType.value),
    },
    feeData: {
      networkFee: 0,
      gatewayFee: 0,
      receivedRate: 0,
      quoteValue: 0,
      isLoading: true,
    },
    otpData: {
      email: null,
      mobile: null,
    },
  }),
  actions: {
    setCoinType(coin) {
      this.coinData.coinType = coin;
      bindLocalStorage("coinType", this.coinData.coinType);
    },
    setRequiredFieldByKey(key, isRequired) {
      this.form[key].required = isRequired;
    },
    setPaymentServiceProvider(value) {
      this.form.payment.value = value;
      bindLocalStorage(this.$id, this.form);
    },
    setPspTypeCode(value) {
      // Working of this function can be changed to include other parameters of the Payment
      this.form.payment.pspTypeCode = value;
    },
    setPspRequireMobile(value) {
      this.form.payment.require_mobile = value;
    },
    async updateValue(field, val) {
      if (!Object.getOwnPropertyDescriptor(this.form, field)) {
        return;
      }

      if (Object.getOwnPropertyDescriptor(this.form[field], "validator")) {
        const globalStore = useGlobalStore();
        const { validator } = this.form[field];
        let validatorBinding;

        switch (field) {
          case "walletAddress":
            if (
              this.findAllBlockchainCodesByCoins &&
              this.findAllBlockchainCodesByCoins.length > 0
            ) {
              validatorBinding = await validator(val, [
                this.form.blockchain.value,
              ]);
            }
            break;
          case "fiatAmount":
            validatorBinding = validator(
              val,
              this.form.fiatType.value,
              filterPaymentLimit(
                this.form.fiatType.value,
                this.form.payment.value,
                globalStore.getPayments
              )
            );

            break;
          case "coinAmount":
            validatorBinding = validator(
              val,
              this.form.coinType.value,
              this.form.blockchain.value,
              this.coinData.data
            );
            break;
          case "walletAddressTag":
            validatorBinding = await validator(
              val,
              this.getWalletMemoByBlockchain
            );
            break;

          default:
            validatorBinding = validator(field, val);
        }

        this.form[field].error = validatorBinding;
      }

      this.form[field].value = val;
      bindLocalStorage(this.$id, this.form);
    },

    updateFeeData(key, value) {
      this.feeData[key] = value;
    },
    async fetchBuyFiats() {
      if (this.fiatData.data.length > 0) {
        return this.fiatData.data;
      }

      const fiats = await getFiats("nft-buy");
      return this.setFiatData(fiats.data.data.fiats);
    },
    async fetchBuyCoins() {
      if (this.coinData.data.length > 0) {
        return this.coinData.data;
      }

      const coins = await getCoins("nft-buy");
      return this.setCoinData(coins.data.data.coins);
    },
    setCoinData(coinResponse) {
      this.coinData.data = coinResponse;
      bindLocalStorage("coinData", this.coinData.data);
      return this.coinData.data;
    },
    setFiatData(fiatResponse) {
      this.fiatData.data = fiatResponse;
      bindLocalStorage("fiatData", this.fiatData.data);
      return this.fiatData.data;
    },
    setLockCoin() {
      this.form.lockFiat.value = false;
    },
    async triggerWalletValidation(walletAddress, blockchain) {
      const { validator } = this.form.walletAddress;

      if (
        this.findAllBlockchainCodesByCoins &&
        this.findAllBlockchainCodesByCoins.length > 0
      ) {
        this.form.walletAddress.error = await validator(walletAddress, [
          blockchain,
        ]);
      }
    },
  },
  getters: {
    formHasErrors: (store) => formHasErrors(store),
    getErrorInForm: (store) => getErrorInForm(store),
    getErrorByField: (store) => (field) => getErrorByField(store)(field),
    getFormPayload: (store) => store.form && getFormPayload(store),
    findAllBlockchainCodesByCoins() {
      // const coinData = store.getActiveCoinData;
      const activeCoin = this.coinData.coinType;
      const coinData = getActiveCoinData(this.coinData.data, activeCoin);

      if (!coinData) return [];
      return coinData.blockchains.map((el) => el.code);
    },

    getWalletMemoByBlockchain(store) {
      if (!store.coinData) {
        return false;
      }

      const activeCoin = store.form.coinType.value;
      const activeBlockchain = store.form.blockchain.value;
      const activeCoinData = getActiveCoinData(store.coinData.data, activeCoin);

      if (!activeCoinData) {
        return false;
      }

      const activeBlockchainData = activeCoinData.blockchains.find(
        (el) => el.code === activeBlockchain
      );

      if (!activeBlockchainData || !activeBlockchainData.address_tag.name) {
        return false;
      }

      return activeBlockchainData.address_tag;
    },
    getFiatType(store) {
      if (!store.form?.fiatType) {
        return null;
      }
      return store.form.fiatType.value;
    },
    getCoinType(store) {
      if (!store.form?.coinType) {
        return null;
      }
      return store.form.coinType.value;
    },
    getRestrictedCountriesByActiveCoin() {
      const { coinType, data } = this.coinData;
      const coinData = getActiveCoinData(data, coinType);
      if (!coinData) return [];
      return coinData?.restrictions?.country;
    },
    getPaymentObject(store) {
      if (!store.form?.payment) {
        return null;
      }
      return store.form.payment;
    },
    getPaymentProvider(store) {
      const globalStore = useGlobalStore();

      return filterPaymentProvider(
        globalStore.global.payments,
        store.form.payment.value
      );
    },
  },
});
